import {
  Button,
  Combobox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Option,
  OptionGroup,
  Spinner,
  Textarea,
} from "@fluentui/react-components";
import {
  CheckmarkRegular,
  Document20Regular,
  DismissRegular,
} from "@fluentui/react-icons";
import { getCategories } from "api";
import {
  adminCreateDeliveryMethodConfig,
  adminUpdateDeliveryMethodConfig,
} from "apps/project-iq/api";
import { METHOD_LABELS } from "apps/project-iq/utils/diagnosticUtils";
import { ALLOWED_FILE_EXTENSIONS } from "common/constants";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { uploadFilesToStorage } from "utils/blobUtils";
import { IFile } from "models/IFile";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import { useManageDeliveryMethodsStyles } from "./ManageDeliveryMethods.styles";
import { IEditDeliveryMethodDialogProps } from "./ManageDeliveryMethods.types";

export const EditDeliveryMethodDialog = ({
  open,
  method,
  config,
  onSave,
  onDismiss,
}: IEditDeliveryMethodDialogProps): JSX.Element => {
  const styles = useManageDeliveryMethodsStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const fileCardStyles = useSharedFileCardStyles();
  const surfaceRef = useRef<HTMLDivElement>(null);

  const [promptText, setPromptText] = useState<string>("");
  const [selectedKnowledgeComponentIds, setSelectedKnowledgeComponentIds] =
    useState<string[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<IFile[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<
    { tempId: string; file: File }[]
  >([]);
  const [uploadDocumentError, setUploadDocumentError] = useState<string>("");
  const [filteredCategories, setFilteredCategories] = useState<ICategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string>("");

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setIsLoading(true);
      setIsSaving(false);
      setSaveError("");
      setUploadDocumentError("");
      setPromptText(config?.prompt ?? "");
      setSelectedKnowledgeComponentIds(config?.knowledgeComponentIds ?? []);
      setUploadedDocuments(config?.documents ?? []);

      try {
        const categories: ICategory[] = await getCategories(true);
        setFilteredCategories(
          (categories ?? []).filter((c) => (c.tags ?? []).length > 0),
        );
      } catch {
        setSaveError("Failed to load knowledge components. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [open, config]);

  const allTags = useMemo<ITag[]>(
    () =>
      filteredCategories.flatMap((c) =>
        (c.tags ?? []).map((t) => ({ ...t, category: c })),
      ),
    [filteredCategories],
  );

  const selectedKcLabel = useMemo(
    () =>
      selectedKnowledgeComponentIds
        .map((id) => allTags.find((t) => t.id === id)?.name ?? "")
        .filter(Boolean)
        .join(", "),
    [selectedKnowledgeComponentIds, allTags],
  );

  const handleSave = async (): Promise<void> => {
    setIsSaving(true);
    setSaveError("");
    try {
      const payload = {
        method,
        prompt: promptText || null,
        knowledgeComponentIds: selectedKnowledgeComponentIds,
        documents: uploadedDocuments,
      };
      if (config === null) {
        await adminCreateDeliveryMethodConfig(payload);
      } else {
        await adminUpdateDeliveryMethodConfig(method, payload);
      }
      onSave();
    } catch {
      setSaveError("Save failed. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileSelect = async (files: File[]): Promise<void> => {
    setUploadDocumentError("");
    for (const file of files) {
      const tempId = crypto.randomUUID();
      setPendingDocuments((prev) => [...prev, { tempId, file }]);
      try {
        const uploadedFiles = await uploadFilesToStorage(
          [{ id: tempId, file }],
          "delivery_method_config",
          method,
          false,
          "",
        );
        if (uploadedFiles?.length) {
          setUploadedDocuments((prev) => [...prev, uploadedFiles[0]]);
        }
      } catch {
        setUploadDocumentError("Failed to upload file. Please try again.");
      } finally {
        setPendingDocuments((prev) => prev.filter((p) => p.tempId !== tempId));
      }
    }
  };

  const handleRemoveDocument = (documentId?: string): void => {
    setUploadedDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) onDismiss();
      }}
    >
      <DialogSurface
        ref={surfaceRef}
        className={styles.formDialogSurface}
        tabIndex={-1}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {METHOD_LABELS[method]} — Configure Prompt
          </DialogTitle>

          <DialogContent className={styles.formDialogContent}>
            {isSaving && (
              <div className={contentStyles.formLoadingOverlay}>
                <Spinner label="Saving..." />
              </div>
            )}

            {saveError && (
              <div role="alert" className={styles.errorAlert}>
                {saveError}
              </div>
            )}

            {isLoading ? (
              <div className={styles.formLoadingContainer}>
                <Spinner label="Loading..." />
              </div>
            ) : (
              <div className={styles.formFields}>
                <Field
                  label="System Prompt"
                  hint="Enter the prompt text for this delivery method"
                  className={styles.dialogField}
                >
                  <Textarea
                    value={promptText}
                    onChange={(_, data) => setPromptText(data.value)}
                    placeholder="Enter your prompt here..."
                    rows={8}
                    disabled={isSaving}
                    aria-label="System Prompt"
                  />
                </Field>

                <Field
                  label="Knowledge Components"
                  className={styles.dialogField}
                >
                  <Combobox
                    multiselect
                    freeform={false}
                    selectedOptions={selectedKnowledgeComponentIds}
                    value={selectedKcLabel}
                    placeholder="Select knowledge components..."
                    onOptionSelect={(_, data) =>
                      setSelectedKnowledgeComponentIds(
                        data.selectedOptions as string[],
                      )
                    }
                    disabled={isSaving}
                  >
                    {filteredCategories.map((category) => (
                      <OptionGroup key={category.id} label={category.title}>
                        {(category.tags ?? []).map((tag) => (
                          <Option key={tag.id} value={tag.id ?? ""}>
                            {tag.name}
                          </Option>
                        ))}
                      </OptionGroup>
                    ))}
                  </Combobox>
                </Field>

                <Field label="Documents" className={styles.dialogField}>
                  <>
                    <FileDropzone
                      onFilesSelected={handleFileSelect}
                      acceptedExtensions={ALLOWED_FILE_EXTENSIONS.map((ext) =>
                        ext.toLowerCase(),
                      )}
                      multiple
                      disabled={isSaving}
                      onValidationError={setUploadDocumentError}
                    />
                    {uploadDocumentError && (
                      <div role="alert" className={styles.errorAlert}>
                        {uploadDocumentError}
                      </div>
                    )}
                    {(uploadedDocuments.length > 0 ||
                      pendingDocuments.length > 0) && (
                      <div className={styles.documentFileList}>
                        {uploadedDocuments.map((doc) => {
                          const ext =
                            doc.filename.split(".").pop()?.toUpperCase() ?? "";
                          return (
                            <div
                              key={doc.id}
                              className={styles.documentFileCard}
                            >
                              <div
                                className={styles.documentFileIconBox}
                                aria-hidden="true"
                              >
                                <Document20Regular />
                              </div>
                              <div className={styles.documentFileMeta}>
                                <span className={styles.documentFileName}>
                                  {doc.filename}
                                </span>
                                {ext && (
                                  <span className={styles.documentFileDetails}>
                                    {ext}
                                  </span>
                                )}
                              </div>
                              <Button
                                appearance="subtle"
                                icon={<DismissRegular aria-hidden="true" />}
                                className={fileCardStyles.fileDeleteButton}
                                onClick={() => handleRemoveDocument(doc.id)}
                                disabled={isSaving}
                                aria-label={`Remove ${doc.filename}`}
                              />
                            </div>
                          );
                        })}
                        {pendingDocuments.map(({ tempId, file }) => {
                          const ext =
                            file.name.split(".").pop()?.toUpperCase() ?? "";
                          return (
                            <div
                              key={tempId}
                              className={styles.documentFileCard}
                            >
                              <div
                                className={styles.documentFileIconBox}
                                aria-hidden="true"
                              >
                                <Spinner size="tiny" />
                              </div>
                              <div className={styles.documentFileMeta}>
                                <span className={styles.documentFileName}>
                                  {file.name}
                                </span>
                                {ext && (
                                  <span className={styles.documentFileDetails}>
                                    {ext}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                </Field>
              </div>
            )}
          </DialogContent>
        </DialogBody>

        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip="Save"
            dimmed={isLoading || isSaving}
            disabled={isSaving || isLoading}
            onClick={() => void handleSave()}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
